import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  checkTrackInfo,
  getSystemTrackInfo,
  isSystemTrack,
  // upperFirst,
} from "../../utils/utils";
import { apiClient } from "../../api/api";
import { logNotFoundTrackInfo } from "../../api/adminService";
import { defaultRadioInfoByType } from "../../utils/constants";
import {
  getSavedPlaybackRate,
  PLAYBACK_RATE_VARIANTS,
  savePlaybackRate,
} from "../../utils/playbackRate";

export const fetchTrackDataApi = createAsyncThunk(
  "radioInfo/fetchTrackDataApi",
  async ({ type, message, trackId }, storeAPI) => {
    if (isSystemTrack(message)) {
      return {
        type,
        track: getSystemTrackInfo(type, message),
        trackName: message,
        isSystemTrack: true,
      };
    }

    try {
      const data = await apiClient
        .get(`track`, {
          params: { title: message, id: trackId || null },
        })
        .then((res) => res.data.data);

      return { track: data, type, trackName: message };
    } catch (error) {
      if (error.response?.status === 404) {
        logNotFoundTrackInfo(message);
      }
      throw error;
    }
  },
);

const initialState = {
  isPlaying: false,
  progress: 0,
  playbackRate: getSavedPlaybackRate(),
  loaded: 0,
  duration: 0,
  volume: 0,
  audioType: null, // media || stream
  currentContent: {
    _id: "",
    title: "",
    track: "",
    artist: "",
    wikipedia: "",
    genre: "",
    release: "",
    cover: { big: "", small: "" },
    userRating: null,
    spotify: {
      url: "",
    },
    yandexMusic: {
      url: "",
    },
    appleMusic: {
      url: "",
    },
  },
  currentMediaLink: null,
  currentStreamType: null, // live || mc || rock || ...
  currentEntity: null, // release || track
};

const playerSlice = createSlice({
  name: "player",
  initialState,
  reducers: {
    playMedia: (state, action) => {
      state.isPlaying = true;
      state.audioType = "media";
      state.currentEntity = "release";
      state.currentStreamType = null;
      state.progress = 0;
      if (action.payload) state.currentMediaLink = action.payload;
    },
    pause: (state) => {
      state.isPlaying = false;
    },
    setCurrentStreamType: (state, action) => {
      state.currentStreamType = action.payload;
    },
    setPlaying: (state, action) => {
      state.isPlaying = action.payload;
    },
    setCurrentContent: (state, action) => {
      if (
        process.env.NODE_ENV === "development" &&
        action.payload.textFileLink
      ) {
        const newTextFileLink =
          window.location.origin + action.payload.textFileLink;

        const updatedPayload = {
          ...action.payload,
          textFileLink: newTextFileLink,
        };

        state.currentContent = updatedPayload;
      } else {
        state.currentContent = action.payload;
      }
    },
    setCurrentMediaLink: (state, action) => {
      state.currentMediaLink = action.payload;
    },
    setAudioType: (state, action) => {
      state.audioType = action.payload;
    },
    setProgress(state, action) {
      state.progress = action.payload;
    },
    setLoaded(state, action) {
      state.loaded = action.payload;
    },
    setDuration(state, action) {
      state.duration = action.payload;
    },
    setCurrentEntity: (state, action) => {
      state.currentEntity = action.payload;
    },
    setVolume: (state, action) => {
      state.volume = action.payload;
    },
    togglePlayer: (state) => {
      state.isPlaying = !state.isPlaying;
    },
    setPlaybackRate: (state, action) => {
      state.playbackRate = action.payload;
      savePlaybackRate(action.payload);
    },
    cyclePlaybackRate: (state) => {
      const currentIndex = PLAYBACK_RATE_VARIANTS.indexOf(state.playbackRate);
      const nextIndex = (currentIndex + 1) % PLAYBACK_RATE_VARIANTS.length;
      const newRate = PLAYBACK_RATE_VARIANTS[nextIndex];
      state.playbackRate = newRate;
      savePlaybackRate(newRate);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTrackDataApi.fulfilled, (state, action) => {
        let trackInfo = action.payload.track;

        if (!action.payload.isSystemTrack) {
          checkTrackInfo(trackInfo);
        }

        state.currentContent = trackInfo;
      })

      .addCase(fetchTrackDataApi.rejected, (state, action) => {
        /** @type {string} */
        const currentTrack = action.meta.arg.message ?? "";
        const trackData = currentTrack.split("-");
        const trackType = action.meta.arg.type;

        if (trackData.length === 2 && trackType) {
          const [artist, trackName] = trackData;
          const trackInfo = {
            ...defaultRadioInfoByType[trackType]?.aboutTrack,
            artist,
            track: trackName,
            // genre: upperFirst(trackType),
          };

          state.currentContent = trackInfo;
        }
      });
  },
});

export const {
  setProgress,
  setDuration,
  setPlaying,
  setLoaded,
  playMedia,
  pause,
  setCurrentContent,
  setCurrentStreamType,
  setAudioType,
  setCurrentMediaLink,
  setVolume,
  setCurrentEntity,
  togglePlayer,
  setPlaybackRate,
  cyclePlaybackRate,
} = playerSlice.actions;

export default playerSlice.reducer;
